var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { IVector3 } from '../api.js';
import { ObjectHandle } from '../common.js';
import { RectangleAtlas } from './RectangleAtlas.js';
import { Vector3 as BABYLON_Vector3 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { RENDER_LAYER_FORWARD } from '../babylon/RenderLayer.js';
import { TriangleCustomMesh } from './CustomMesh.js';
import { Deprecated } from '../utils/Deprecated.js';
function mod(n, m) {
    return ((n % m) + m) % m;
}
const SKIRTING_HEIGHT = 0.056;
const SKIRTING_DEPTH = 0.012;
export class BakeSettings {
    constructor(samples_per_frame, samples, texture_dimensions) {
        this.samples_per_frame = samples_per_frame || 8;
        this.samples = samples || 512;
        this.texture_height = texture_dimensions || 1024;
        this.texture_width = texture_dimensions || 1024;
    }
}
function calculate_skirting_segments(wall_length, doors) {
    let cap_start = false;
    const segments = [];
    let offset = 0;
    for (const door of doors) {
        // Each skirting segments runs from the current offset to the start of the next door.
        segments.push({
            start_offset: offset,
            end_offset: door.offset,
            cap_start: cap_start,
            cap_end: true
        });
        // The start of the next skirting segment is the other side of the door.
        offset = door.offset + door.size.x;
        cap_start = true;
    }
    segments.push({
        start_offset: offset,
        end_offset: wall_length,
        cap_start: cap_start,
        cap_end: false
    });
    return segments;
}
function intersects(a, b, c, d) {
    const a1 = a.y - b.y;
    const b1 = b.x - a.x;
    const c1 = a.x * b.y - b.x * a.y;
    const a2 = c.y - d.y;
    const b2 = d.x - c.x;
    const c2 = c.x * d.y - d.x * c.y;
    const det = a1 * b2 - a2 * b1;
    if (det === 0) {
        return undefined;
    }
    const x = (b2 * c1 - b1 * c2) / -det; // (b2*a1 - b1*a2)
    const y = (a2 * c1 - a1 * c2) / det; // (a2*b1 - a1*b2)
    const minx = Math.min(c.x, d.x) - 0.00001;
    const maxx = Math.max(c.x, d.x) + 0.00001;
    const miny = Math.min(c.y, d.y) - 0.00001;
    const maxy = Math.max(c.y, d.y) + 0.00001;
    if (x > maxx || x < minx || y > maxy || y < miny)
        return undefined;
    const distance = -(x - a.x);
    if (distance <= 0)
        return undefined;
    return { x: x, y: y, distance: distance };
}
function insert_hole_vertices(vertices, hole_vertices) {
    // Build a list of all line segments in the outer polygon as well as the hole.
    const segments = vertices.map((v, i) => {
        return {
            a: v,
            b: vertices[mod(i + 1, vertices.length)]
        };
    });
    const left_ray = {
        a: hole_vertices[0],
        b: { x: hole_vertices[0].x - 1, y: hole_vertices[0].y }
    };
    let closest_distance = Infinity;
    let closest_index = -1;
    let v = vertices[0]; // dummy value
    for (let i = 0; i < segments.length; i++) {
        const segment = segments[i];
        const res = intersects(left_ray.a, left_ray.b, segment.a, segment.b);
        if (res !== undefined) {
            if (res.distance < closest_distance) {
                closest_distance = res.distance;
                closest_index = i;
                v = { x: res.x, y: res.y };
            }
        }
    }
    const closest_a = vertices[closest_index];
    const closest_b = vertices[mod(closest_index + 1, vertices.length)];
    // When we hit the closest segment at one of the vertices, we don't want to insert an extra vertex splitting that segment.
    if (Math.abs(v.x - closest_a.x) < 0.01 &&
        Math.abs(v.y - closest_a.y) < 0.01) {
        // If we intersect the segment at the first index, go from that vertex, to our hole vertices, and back
        vertices.splice(closest_index + 1, 0, ...hole_vertices, closest_a);
    }
    else if (Math.abs(v.x - closest_b.x) < 0.01 &&
        Math.abs(v.y - closest_b.y) < 0.01) {
        // If we intersect the second vertex of the segment, insert the whole vertices in connection to that vertex.
        vertices.splice(closest_index + 2, 0, ...hole_vertices, closest_b);
    }
    else {
        // If we intersect the segment somewhere in the middle, we split the segment at our intersection point and connect the new vertex with the hole.
        vertices.splice(closest_index + 1, 0, v, ...hole_vertices, v);
    }
}
function insert_hole_for_windows(w, wall_vertices) {
    const pos = w.position;
    const size = w.size;
    const vertices = [
        { x: pos.x, y: pos.y + size.y },
        { x: pos.x + size.x, y: pos.y + size.y },
        { x: pos.x + size.x, y: pos.y },
        { x: pos.x, y: pos.y },
        { x: pos.x, y: pos.y + size.y }
    ];
    insert_hole_vertices(wall_vertices, vertices);
}
function get_materials(object_ids, viewer) {
    const material_id_set = new Set(object_ids.map((id) => viewer.get_material(id)));
    return [...material_id_set];
}
export class Room {
    constructor(viewer, schema, _wall_material = null, _wallskirt_material = null, _floor_material = null, _ceiling_material = null) {
        this.schema = schema;
        this._wall_material = _wall_material;
        this._wallskirt_material = _wallskirt_material;
        this._floor_material = _floor_material;
        this._ceiling_material = _ceiling_material;
        // room geometry
        this._walls = [];
        this._shadow_walls = [];
        this._wallskirts = [];
        this._ceiling = -1;
        this._shadow_ceiling = -1;
        this._floor = -1;
        this.wall_segments_normals = new Array(); // Will hold normals for each wall segment
        this.wall_segments_center_points = new Array(); // Worldspace position of each wall segments center
        this.wall_corners_point = new Array(); // World space position of each corner
        this.wall_corners_normal = new Array(); // World space normal of each corner
        this.wall_corners_tangent = new Array(); // World space tangent of each corner
        this._door_transforms = [];
        this._window_transforms = [];
        this._room_min = { x: 0, y: 0, z: 0 };
        this._room_max = { x: 0, y: 0, z: 0 };
        this._area_lights = [];
        this._atlas_width = -1;
        this._atlas_height = -1;
        this._async_calculate_lighting_job = Promise.resolve();
        this._viewer = viewer;
        this.generate_geometry();
        // Calculate normal and center for every wall segment to be able to calculate visibility (or rather which side faces the camera) later on
        for (let i = 0; i < schema.wall_vertices.length; i++) {
            const coord1 = schema.wall_vertices[i];
            const coord2 = schema.wall_vertices[(i + 1) % schema.wall_vertices.length];
            this.wall_corners_point.push(coord1);
            // Calculate the wall normal from the two points that corresponds to its start and end
            const dx = coord2.x - coord1.x;
            const dz = coord2.z - coord1.z;
            const normal = { x: -dz, y: 0, z: dx };
            // Normalize it
            const normal_length = Math.sqrt(Math.pow(normal.x, 2.0) + Math.pow(normal.z, 2.0));
            normal.x /= normal_length;
            normal.z /= normal_length;
            this.wall_segments_normals.push(normal);
            // Calculate the wall segments center point, this is where we'll put the walls frame, offset by the wall segment normal
            const center = {
                x: (coord1.x + coord2.x) * 0.5,
                y: 0,
                z: (coord1.z + coord2.z) * 0.5
            };
            this.wall_segments_center_points.push(center);
        }
        const vector3_up = { x: 0, y: 1, z: 0 };
        for (let i = 0; i < this.wall_segments_normals.length; i++) {
            const num_walls = this.wall_segments_normals.length;
            this.wall_corners_normal.push(IVector3.normalize(IVector3.add(this.wall_segments_normals[i], this.wall_segments_normals[(i - 1 + num_walls) % num_walls])));
            this.wall_corners_tangent.push(IVector3.cross(this.wall_corners_normal[i], vector3_up));
        }
    }
    generate_geometry() {
        const atlas = new RectangleAtlas(30);
        const min = this.schema.wall_vertices.reduce((acc, v) => {
            return {
                x: Math.min(acc.x, v.x),
                y: Math.min(acc.y, v.y),
                z: Math.min(acc.z, v.z),
                w: Math.min(acc.w, v.w)
            };
        }, {
            x: Number.POSITIVE_INFINITY,
            y: Number.POSITIVE_INFINITY,
            z: Number.POSITIVE_INFINITY,
            w: Number.POSITIVE_INFINITY
        });
        const max = this.schema.wall_vertices.reduce((acc, v) => {
            return {
                x: Math.max(acc.x, v.x),
                y: Math.max(acc.y, v.y),
                z: Math.max(acc.z, v.z),
                w: Math.max(acc.w, v.w)
            };
        }, {
            x: Number.NEGATIVE_INFINITY,
            y: Number.NEGATIVE_INFINITY,
            z: Number.NEGATIVE_INFINITY,
            w: Number.NEGATIVE_INFINITY
        });
        this._room_min = { x: min.x, y: min.y, z: min.z };
        this._room_max = { x: max.x, y: max.y + max.w, z: max.z };
        const floorsize_x = max.x - min.x;
        const floorsize_z = max.z - min.z;
        const floor_vertices = this.schema.wall_vertices.map((v) => ({
            x: v.x,
            y: v.z
        }));
        const floor_atlas = atlas.add_rectangle(floorsize_x, floorsize_z);
        const ceiling_atlas = atlas.add_rectangle(floorsize_x, floorsize_z);
        const wall_atlas = [];
        const window_table = {};
        const window_table_for_shadow_wall = {};
        this.schema.windows.forEach((window) => {
            window_table[window.wall_index] = window_table[window.wall_index] || [];
            window_table[window.wall_index].push(window);
            window_table_for_shadow_wall[window.wall_index] = window_table_for_shadow_wall[window.wall_index] || [];
            window_table_for_shadow_wall[window.wall_index].push(window);
        });
        const door_table = {};
        this.schema.doors.forEach((door) => {
            door_table[door.wall_index] = door_table[door.wall_index] || [];
            door_table[door.wall_index].push(door);
        });
        Object.values(door_table).forEach((doors) => {
            doors.sort((a, b) => {
                return a.offset - b.offset;
            });
        });
        // The angle between the walls on the inside of the room.
        const angles = [];
        for (let i = 0; i < floor_vertices.length; i++) {
            angles.push(inner_angle(floor_vertices[i], floor_vertices[mod(i + 1, floor_vertices.length)], floor_vertices[mod(i - 1, floor_vertices.length)]));
        }
        const skirting_joint_corrections = angles.map((a) => {
            // cot(a) = tan(PI/2 - a)
            // cot(a) = cos(a) / sin(a)
            const h = (Math.PI - a) / 2;
            return Math.tan(h) * SKIRTING_DEPTH;
        });
        let max_wall_height = 0.0;
        const skirting_uvs = [];
        const wall_skirting_infos = [];
        const skirting_positions = []; // new Float32Array(this.schema.wall_vertices.length * 8 * 3)
        const skirting_uv1 = []; // = new Float32Array(this.schema.wall_vertices.length * 8 * 2)
        // Setup walls and skirtings
        for (let i = 0; i < this.schema.wall_vertices.length; i++) {
            const prev = mod(i - 1, this.schema.wall_vertices.length);
            const v0 = new BABYLON_Vector3(this.schema.wall_vertices[prev].x, 0, this.schema.wall_vertices[prev].z);
            const v1 = new BABYLON_Vector3(this.schema.wall_vertices[i].x, 0, this.schema.wall_vertices[i].z);
            const wall_length = v0.subtract(v1).length();
            wall_skirting_infos.push(calculate_skirting_segments(wall_length, door_table[i] || []));
            const wall_height = Math.max(this.schema.wall_vertices[prev].w, this.schema.wall_vertices[i].w);
            wall_atlas.push(atlas.add_rectangle(wall_length, wall_height));
            max_wall_height = Math.max(wall_height, max_wall_height);
            skirting_uvs.push(wall_skirting_infos[i].map((info) => {
                const length = info.end_offset - info.start_offset;
                return {
                    top: atlas.add_rectangle(length, SKIRTING_DEPTH),
                    side: atlas.add_rectangle(length +
                        (Number(info.cap_start) + Number(info.cap_end)) *
                            SKIRTING_DEPTH, SKIRTING_HEIGHT)
                };
            }));
        }
        atlas.atlas();
        {
            const { width, height } = atlas.get_resolution();
            this._atlas_width = width;
            this._atlas_height = height;
        }
        this._floor = tesselate_polygon_with_ear_clipping(this._viewer, floor_vertices, floor_atlas, { x: 1, y: 0, z: 0 }, { x: 0, y: 0, z: 1 }, false, this._floor_material);
        const floor = new ObjectHandle(this._floor, this._viewer);
        floor.cast_shadow = false;
        floor.receive_shadow = true;
        floor.scale.y = -1;
        // create two ceilings, the purpose for the second one is to cast shadow
        for (let i = 0; i < 2; i++) {
            const ceiling_id = tesselate_polygon_with_ear_clipping(this._viewer, floor_vertices, ceiling_atlas, { x: 1, y: 0, z: 0 }, { x: 0, y: 0, z: 1 }, i === 1, // false when i = 0; true when i = 1 which means that only cast shadow
            this._ceiling_material);
            this._viewer.set_cast_shadow(ceiling_id, false);
            this._viewer.set_receive_shadow(ceiling_id, true);
            const ceiling = new ObjectHandle(ceiling_id, this._viewer);
            ceiling.position.y = max_wall_height; // This is a fudge factor to make ceiling water-tight.
            ceiling.cast_shadow = false;
            ceiling.receive_shadow = true;
            if (i === 1) {
                this._shadow_ceiling = ceiling_id;
            }
            else {
                this._ceiling = ceiling_id;
            }
        }
        const indices = [];
        // Ceiling area light
        this._area_lights.push({
            intensity: this.schema.ceiling_light_intensity !== undefined
                ? this.schema.ceiling_light_intensity
                : 0.8,
            roty: Math.PI * 0.5,
            rotz: Math.PI * 0.5,
            center: {
                x: (min.x + max.x) / 2,
                y: max_wall_height,
                z: (min.z + max.z) / 2
            },
            size: { x: floorsize_z, y: floorsize_x },
            color: this.schema.ceiling_light_color !== undefined
                ? this.schema.ceiling_light_color
                : { r: 1.0, g: 1.0, b: 0.9 }
        });
        // Floor area light
        this._area_lights.push({
            intensity: this.schema.floor_light_intensity !== undefined
                ? this.schema.floor_light_intensity
                : 0.4,
            roty: Math.PI * 0.5,
            rotz: -Math.PI * 0.5,
            center: {
                x: (min.x + max.x) / 2,
                y: 0,
                z: (min.z + max.z) / 2
            },
            size: { x: floorsize_z, y: floorsize_x },
            color: this.schema.floor_light_color !== undefined
                ? this.schema.floor_light_color
                : { r: 0.95, g: 0.95, b: 1.0 }
        });
        // Set double-side for the wall plane, enable to case shadow for walls
        // Index i from 0 to this.schema.wall_vertices.length - 1 are walls with normal toward inside the room.
        // Index i from this.schema.wall_vertices.length to this.schema.wall_vertices.length * 2 - 1 are walls with normal toward outside the room.
        for (let i = 0; i < this.schema.wall_vertices.length * 2; i++) {
            const prev = mod(i - 1, this.schema.wall_vertices.length);
            const wall_index = mod(i, this.schema.wall_vertices.length);
            const v0 = new BABYLON_Vector3(this.schema.wall_vertices[prev].x, 0, this.schema.wall_vertices[prev].z);
            const v1 = new BABYLON_Vector3(this.schema.wall_vertices[wall_index].x, 0, this.schema.wall_vertices[wall_index].z);
            let shadow_wall = false;
            // set wall invisible to keep see through functionality
            if (i >= this.schema.wall_vertices.length) {
                shadow_wall = true;
            }
            const wall_delta = v1.subtract(v0);
            const wall_length = wall_delta.length();
            wall_delta.normalizeFromLength(wall_length);
            const wall_height = Math.max(this.schema.wall_vertices[prev].w, this.schema.wall_vertices[wall_index].w);
            const doors = door_table[wall_index];
            const door_vertices = [];
            if (doors && !shadow_wall) {
                doors.forEach((door) => {
                    if (door.offset + door.size.x > wall_length || door.offset < 0) {
                        throw new Error('Invalid door placement.');
                    }
                    door_vertices.push({ x: door.offset, y: 0 });
                    door_vertices.push({ x: door.offset, y: door.size.y });
                    door_vertices.push({ x: door.offset + door.size.x, y: door.size.y });
                    door_vertices.push({ x: door.offset + door.size.x, y: 0 });
                    const area_light = {
                        intensity: 1.0,
                        roty: Math.atan2(wall_delta.x, wall_delta.z) + Math.PI * 0.5,
                        rotz: 0.0,
                        center: v0.add(wall_delta.scale(door.offset + door.size.x * 0.5)),
                        size: door.size
                    };
                    this._area_lights.push(area_light);
                    this._door_transforms.push({
                        worldposition: area_light.center,
                        rotation: { x: 0, y: area_light.roty, z: 0 }
                    });
                });
            }
            const wall_vertices = [
                { x: 0.0, y: 0.0 },
                ...door_vertices,
                { x: wall_length, y: 0.0 },
                { x: wall_length, y: wall_height },
                { x: 0.0, y: wall_height }
            ];
            const windows = window_table[wall_index];
            const shadow_windows = shadow_wall ? window_table_for_shadow_wall[wall_index] : undefined;
            if (windows) {
                windows.sort();
                windows.forEach((w) => {
                    insert_hole_for_windows(w, wall_vertices);
                    const window_center = v0.add(wall_delta.scale(w.position.x + w.size.x * 0.5));
                    window_center.addInPlaceFromFloats(0, w.position.y + w.size.y * 0.5, 0);
                    const area_light = {
                        intensity: 5.0,
                        roty: Math.atan2(wall_delta.x, wall_delta.z) + Math.PI * 0.5,
                        rotz: 0.0,
                        center: window_center,
                        size: w.size
                    };
                    this._area_lights.push(area_light);
                    this._window_transforms.push({
                        worldposition: area_light.center,
                        rotation: { x: 0, y: area_light.roty, z: 0 }
                    });
                });
            }
            else if (shadow_windows) {
                shadow_windows.sort();
                shadow_windows.forEach((w) => {
                    insert_hole_for_windows(w, wall_vertices);
                });
            }
            const wall_id = tesselate_polygon_with_ear_clipping(this._viewer, wall_vertices, wall_atlas[wall_index], wall_delta, { x: 0, y: 1, z: 0 }, shadow_wall, this._wall_material);
            this._viewer.set_cast_shadow(wall_id, false);
            this._viewer.set_receive_shadow(wall_id, true);
            const wall = new ObjectHandle(wall_id, this._viewer);
            wall.position.x = v0.x;
            wall.position.z = v0.z;
            if (shadow_wall) {
                this._shadow_walls.push(wall_id);
            }
            else {
                this._walls.push(wall_id);
            }
            const wall_normal = BABYLON_Vector3.Cross(wall_delta, BABYLON_Vector3.Up()).normalize();
            if (this.schema.generate_skirting && !shadow_wall) {
                // Skirting diagram from the side.
                //
                //  # v3    v2
                //  # |     |
                //  # v     v
                //  # +-----+ <-- v1
                //  #       |
                //  #       |
                //  #       |
                //  #       |
                //  #       |
                //  #       +  <-- v0
                //  =============================
                const tx = wall_delta.x; // Wall tangent x
                const tz = wall_delta.z; // Wall tangent z
                for (let skirting_index = 0; skirting_index < wall_skirting_infos[i].length; skirting_index++) {
                    const skirting = wall_skirting_infos[i][skirting_index];
                    const ax = this.schema.wall_vertices[prev].x + skirting.start_offset * tx;
                    const ay = this.schema.wall_vertices[prev].y;
                    const az = this.schema.wall_vertices[prev].z + skirting.start_offset * tz;
                    const bx = this.schema.wall_vertices[prev].x + skirting.end_offset * tx;
                    const by = this.schema.wall_vertices[prev].y;
                    const bz = this.schema.wall_vertices[prev].z + skirting.end_offset * tz;
                    const side_uv = skirting_uvs[i][skirting_index].side.placement;
                    const top_uv = skirting_uvs[i][skirting_index].top.placement;
                    const skirting_joint_offset_a = skirting_joint_corrections[prev];
                    const skirting_joint_offset_b = skirting_joint_corrections[i];
                    let adx = wall_normal.x * SKIRTING_DEPTH;
                    let adz = wall_normal.z * SKIRTING_DEPTH;
                    let bdx = adx;
                    let bdz = adz;
                    const u_size = skirting.end_offset -
                        skirting.start_offset +
                        (Number(skirting.cap_start) + Number(skirting.cap_end)) *
                            SKIRTING_DEPTH;
                    const skirting_cap_uv_length = SKIRTING_DEPTH / u_size;
                    const side_min_u = side_uv.x + (skirting.cap_start ? skirting_cap_uv_length : 0);
                    const side_max_u = side_uv.z - (skirting.cap_end ? skirting_cap_uv_length : 0);
                    // Correct for corners at the start and end of the wall!
                    if (skirting_index === 0) {
                        adx += tx * skirting_joint_offset_a;
                        adz += tz * skirting_joint_offset_a;
                    }
                    if (skirting_index === wall_skirting_infos[i].length - 1) {
                        bdx -= tx * skirting_joint_offset_b;
                        bdz -= tz * skirting_joint_offset_b;
                    }
                    const index_offset = skirting_uv1.length / 2;
                    // Start of wall - Side
                    skirting_positions.push(ax + adx, ay, az + adz);
                    skirting_uv1.push(side_min_u, side_uv.y);
                    skirting_positions.push(ax + adx, ay + SKIRTING_HEIGHT, az + adz);
                    skirting_uv1.push(side_min_u, side_uv.w);
                    // End of wall - Side
                    skirting_positions.push(bx + bdx, by, bz + bdz);
                    skirting_uv1.push(side_max_u, side_uv.y);
                    skirting_positions.push(bx + bdx, by + SKIRTING_HEIGHT, bz + bdz);
                    skirting_uv1.push(side_max_u, side_uv.w);
                    // Start of wall - Top
                    skirting_positions.push(ax + adx, ay + SKIRTING_HEIGHT, az + adz);
                    skirting_uv1.push(top_uv.x, top_uv.y);
                    skirting_positions.push(ax, ay + SKIRTING_HEIGHT, az);
                    skirting_uv1.push(top_uv.x, top_uv.w);
                    // End of wall - Top
                    skirting_positions.push(bx + bdx, by + SKIRTING_HEIGHT, bz + bdz);
                    skirting_uv1.push(top_uv.z, top_uv.y);
                    skirting_positions.push(bx, by + SKIRTING_HEIGHT, bz);
                    skirting_uv1.push(top_uv.z, top_uv.w);
                    indices.push(...[0, 2, 3, 0, 3, 1, 4, 6, 7, 4, 7, 5].map((n) => n + index_offset));
                    if (skirting.cap_start) {
                        skirting_positions.push(ax, ay, az);
                        skirting_uv1.push(side_uv.x, side_uv.y);
                        skirting_positions.push(ax, ay + SKIRTING_HEIGHT, az);
                        skirting_uv1.push(side_uv.x, side_uv.w);
                        indices.push(...[8, 0, 1, 8, 1, 9].map((n) => n + index_offset));
                    }
                    if (skirting.cap_end) {
                        skirting_positions.push(bx, by, bz);
                        skirting_uv1.push(side_uv.z, side_uv.y);
                        skirting_positions.push(bx, by + SKIRTING_HEIGHT, bz);
                        skirting_uv1.push(side_uv.z, side_uv.w);
                        const index_a = 8 + (skirting.cap_start ? 2 : 0);
                        const index_b = 9 + (skirting.cap_start ? 2 : 0);
                        indices.push(...[2, index_a, index_b, 2, index_b, 3].map((n) => n + index_offset));
                    }
                }
            }
            if (skirting_positions.length > 0) {
                const skirtings = this._viewer.create_custom_mesh({
                    positions: new Float32Array(skirting_positions),
                    indices: new Uint32Array(indices),
                    uv1: new Float32Array(skirting_uv1)
                }, this._wallskirt_material);
                this._wallskirts.push(skirtings);
            }
        }
    }
    set_material(wall_material, wallskirt_material, floor_material, ceiling_material) {
        this._wall_material = wall_material;
        this._wallskirt_material = wallskirt_material;
        this._floor_material = floor_material;
        this._ceiling_material = ceiling_material;
        const promises = [];
        promises.push(this._viewer.set_material(this._ceiling, ceiling_material));
        promises.push(this._viewer.set_material(this._floor, floor_material));
        this._walls.forEach((wall_id) => {
            promises.push(this._viewer.set_material(wall_id, wall_material));
        });
        this._wallskirts.forEach((wallskirt_id) => {
            promises.push(this._viewer.set_material(wallskirt_id, wallskirt_material));
        });
        // Some ugly workaround since void !== void[] :(, whatever a void array is ╭〈≖_≖〝〉╮
        return Promise.all(promises).then(() => Promise.resolve());
    }
    create_room_bake_job() {
        return {
            texture_width: this._atlas_width,
            texture_height: this._atlas_height,
            uvset: 1,
            targets: [this.get_floor(), this.get_ceiling()]
                .concat(this.get_walls())
                .concat(this.get_wallskirts()),
            area_lights: this._area_lights
        };
    }
    /**
     * Get materials assigned to the room
     * @returns Array of MaterialIDs. No duplicates
     */
    get_room_materials() {
        return get_materials(this.get_room_objects(), this._viewer);
    }
    dispose_active_lightmaps() {
        const lightmaps = new Set();
        this.get_room_materials().forEach((material_id) => {
            const texture_id = this._viewer.get_lightmap_texture(material_id);
            if (texture_id !== null) {
                lightmaps.add(texture_id);
                this._viewer.set_lightmap_texture(material_id, null);
            }
        });
        lightmaps.forEach((texture_id) => {
            this._viewer.delete_texture(texture_id);
        });
    }
    set_targets_to_have_baked_lightmap(job, lightmap) {
        get_materials(job.targets, this._viewer).forEach((material_id) => {
            this._viewer.set_lightmap_texture(material_id, lightmap);
        });
    }
    calculate_lighting() {
        const job = this.create_room_bake_job();
        const lightmap = this._viewer.add_bake_job_progressive(job);
        this.dispose_active_lightmaps();
        this.set_targets_to_have_baked_lightmap(job, lightmap);
    }
    async_calculate_lighting() {
        const job = this.create_room_bake_job();
        this._async_calculate_lighting_job = this._viewer
            .add_bake_job_async(job)
            .then((lightmap) => {
            this.dispose_active_lightmaps();
            this.set_targets_to_have_baked_lightmap(job, lightmap);
        }, () => {
            console.warn('Lightmap texture is not set properly.');
        });
    }
    get_bounds() {
        return { min: this._room_min, max: this._room_max };
    }
    get_center() {
        return {
            x: (this._room_max.x + this._room_min.x) * 0.5,
            y: (this._room_min.y + this._room_max.y) * 0.5,
            z: (this._room_max.z + this._room_min.z) * 0.5
        };
    }
    get_walls() {
        return this._walls;
    }
    get_shadow_walls() {
        return this._shadow_walls;
    }
    get_door_transform(door_index) {
        return this._door_transforms[door_index];
    }
    get_window_transform(windo_index) {
        return this._window_transforms[windo_index];
    }
    get_wallskirts() {
        return this._wallskirts;
    }
    get_ceiling() {
        return this._ceiling;
    }
    get_shadow_ceiling() {
        return this._shadow_ceiling;
    }
    get_floor() {
        return this._floor;
    }
    get_room_objects() {
        return [this._floor, this._ceiling].concat(this._walls, this._wallskirts);
    }
    dispose() {
        this._async_calculate_lighting_job.catch(() => {
            // if room is disposed while calculating light, catch the error "LightBaker was stopped before completion"
        });
        this.dispose_active_lightmaps();
        // dispose all objects
        this.get_room_objects().forEach((id) => {
            this._viewer.delete_object(id, false);
        });
        this.get_shadow_walls().forEach((wall_id) => {
            this._viewer.delete_object(wall_id, false);
        });
        this._viewer.delete_object(this._shadow_ceiling, false);
    }
    wall_visible_from_position(position) {
        const walls_visible = new Array();
        for (let i = 0; i < this._walls.length; i++) {
            const wall_center_point = this.wall_segments_center_points[i];
            const normal = this.wall_segments_normals[i];
            // Calculate if the camera is behind the wall the frame belongs to, by taking any point on the wall (in this case the planes center) and checking the sign of the dot product between the walls normal and the vector from the cameras position to the point on the wall
            const to_plane = {
                x: wall_center_point.x - position.x,
                y: wall_center_point.y,
                z: wall_center_point.z - position.z
            };
            const dot_product = normal.x * to_plane.x + normal.y * to_plane.y + normal.z * to_plane.z;
            const wall_visible = dot_product < 0;
            walls_visible.push(wall_visible);
        }
        return walls_visible;
    }
}
__decorate([
    Deprecated({ new: 'async_calculate_lighting' })
], Room.prototype, "calculate_lighting", null);
function inner_angle(vertex, previous, next) {
    const x1 = previous.x - vertex.x;
    const y1 = previous.y - vertex.y;
    const x2 = next.x - vertex.x;
    const y2 = next.y - vertex.y;
    let angle = Math.atan2(x1 * y2 - y1 * x2, x1 * x2 + y1 * y2);
    if (angle < 0) {
        angle += Math.PI * 2;
    }
    return angle;
}
function sign(p, v0, v1) {
    return (p.x - v1.x) * (v0.y - v1.y) - (v0.x - v1.x) * (p.y - v1.y);
}
// Test is p is inside the triangle v0v1v2.
function point_in_2d_triangle(p, v0, v1, v2) {
    // Inlude points that lie on the edges, i.e. sign = 0
    const s1 = sign(p, v0, v1);
    if (s1 < 0)
        return false;
    const s2 = sign(p, v1, v2);
    if (s2 < 0)
        return false;
    const s3 = sign(p, v2, v0);
    if (s3 < 0)
        return false;
    // Exclude points that lie on a vertex, i.e. sign = 0 for several edges.
    const zeroes = (s1 === 0 ? 1 : 0) + (s2 === 0 ? 1 : 0) + (s3 === 0 ? 1 : 0);
    if (zeroes > 1)
        return false;
    return true;
}
function tesselate_polygon_with_ear_clipping(viewer, vertices, atlas, base_u, base_v, shadow_only, material) {
    const positions = new Float32Array(vertices.length * 3);
    vertices.forEach((v, i) => {
        positions[i * 3 + 0] = v.x * base_u.x + v.y * base_v.x;
        positions[i * 3 + 1] = v.x * base_u.y + v.y * base_v.y;
        positions[i * 3 + 2] = v.x * base_u.z + v.y * base_v.z;
    });
    const angles = [];
    for (let i = 0; i < vertices.length; i++) {
        angles.push({
            angle: inner_angle(vertices[i], vertices[mod(i + 1, vertices.length)], vertices[mod(i - 1, vertices.length)]),
            vertex: i
        });
    }
    const indices = [];
    while (angles.length > 2) {
        const min = angles.reduce((prev, current, index) => {
            const is_ear = () => {
                if (current.angle > Math.PI)
                    return false;
                const curr = angles[index];
                const prev_index = mod(index - 1, angles.length);
                const next_index = mod(index + 1, angles.length);
                const prev = angles[prev_index];
                const next = angles[next_index];
                const v0 = vertices[curr.vertex];
                const v1 = vertices[next.vertex];
                const v2 = vertices[prev.vertex];
                for (let i = 0; i < angles.length; i++) {
                    if (i === prev_index || i === index || i === next_index)
                        continue;
                    if (point_in_2d_triangle(vertices[angles[i].vertex], v0, v1, v2)) {
                        return false;
                    }
                }
                return true;
            };
            if (current.angle < prev.angle && is_ear()) {
                return Object.assign(Object.assign({}, current), { index: index });
            }
            else {
                return Object.assign(Object.assign({}, prev), { index: prev.index });
            }
        }, { angle: Infinity, vertex: -1, index: -1 });
        const prev = angles[mod(min.index - 1, angles.length)];
        const next = angles[mod(min.index + 1, angles.length)];
        if (shadow_only) {
            // inversed normal
            indices.push(min.vertex, prev.vertex, next.vertex);
        }
        else {
            indices.push(min.vertex, next.vertex, prev.vertex);
        }
        const prevprev = angles[mod(min.index - 2, angles.length)];
        const nextnext = angles[mod(min.index + 2, angles.length)];
        prev.angle = inner_angle(vertices[prev.vertex], vertices[next.vertex], vertices[prevprev.vertex]);
        next.angle = inner_angle(vertices[next.vertex], vertices[nextnext.vertex], vertices[prev.vertex]);
        angles.splice(min.index, 1);
    }
    const uv0 = new Float32Array(vertices.length * 2);
    const uv1 = new Float32Array(vertices.length * 2);
    let xmin = Infinity;
    let xmax = -Infinity;
    let ymin = Infinity;
    let ymax = -Infinity;
    for (let i = 0; i < vertices.length; i++) {
        uv0[i * 2] = vertices[i].x;
        uv0[i * 2 + 1] = vertices[i].y;
        xmax = Math.max(xmax, vertices[i].x);
        ymax = Math.max(ymax, vertices[i].y);
        xmin = Math.min(xmin, vertices[i].x);
        ymin = Math.min(ymin, vertices[i].y);
    }
    const x_size = xmax - xmin;
    const y_size = ymax - ymin;
    for (let i = 0; i < vertices.length; i++) {
        uv1[i * 2 + 0] =
            atlas.placement.x +
                ((vertices[i].x - xmin) / x_size) *
                    (atlas.placement.z - atlas.placement.x);
        uv1[i * 2 + 1] =
            atlas.placement.y +
                ((vertices[i].y - ymin) / y_size) *
                    (atlas.placement.w - atlas.placement.y);
    }
    const mesh = new TriangleCustomMesh(positions, new Uint32Array(indices), null, null, null, uv0, uv1);
    mesh.generate_face_normals();
    const custom_mesh = viewer.create_custom_mesh(mesh.get_mesh_description(), material);
    viewer.set_cast_shadow(custom_mesh, shadow_only !== null && shadow_only !== void 0 ? shadow_only : false, false);
    shadow_only && viewer.remove_from_layer(custom_mesh, RENDER_LAYER_FORWARD);
    return custom_mesh;
}
//# sourceMappingURL=Room.js.map